import React from 'react';
import { guidedTourTypes } from '../registrationFormComponents/data';
import { CheckIcon } from '../../assets/icons';

const GuidedTimeSlots = ({ timeSlots = [], isLoading }) => {
  // Get text color and background color based on tour type using the colors from guidedTourTypes
  const getStyles = (type, filledSlots) => {
    // Find the matching tour type from guidedTourTypes
    const tourType = guidedTourTypes.find((t) => t.value === type);

    // Default styles if type not found
    let textColor = 'text-gray-700';
    let bgColor = 'bg-gray-50';
    let hoverBg = 'hover:bg-gray-100';

    if (tourType) {
      // Convert hex colors to tailwind-compatible classes
      const colorClass = getColorClass(tourType.color);
      const lightColorClass = getColorClass(tourType.color2);

      textColor = `text-${colorClass}`;
      bgColor = `bg-${lightColorClass}`;
      hoverBg = `hover:bg-${colorClass} hover:bg-opacity-20`;
    }

    // If slots are limited, highlight them
    if (filledSlots <= 0) {
      return {
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        hoverBg: 'hover:bg-red-100',
      };
    }

    return { textColor, bgColor, hoverBg };
  };

  // Helper function to convert hex colors to closest Tailwind classes
  const getColorClass = (hexColor) => {
    if (!hexColor) return 'blue-500';

    // Simple mapping based on the three tour types we know about
    if (hexColor.includes('#DF89E5') || hexColor.includes('#df89e5')) {
      return 'pink-500';
    } else if (hexColor.includes('#363B90') || hexColor.includes('#363b90')) {
      return 'indigo-800';
    } else if (hexColor.includes('#4DB14D') || hexColor.includes('#4db14d')) {
      return 'green-600';
    }

    // Default
    return 'blue-500';
  };

  // Skeleton loading component
  const SkeletonTable = () => {
    // Create an array of 4 skeleton time slots rows (8 total rows with extras)
    return (
      <div className="w-full overflow-x-auto shadow-sm rounded-lg">
        <table className="w-full border-collapse">
          <tbody>
            {[...Array(4)].map((_, timeIndex) => (
              <React.Fragment key={timeIndex}>
                {/* Header row skeleton */}
                <tr className="bg-blue-50 border-b border-gray-200">
                  <td
                    rowSpan="2"
                    className="px-4 py-3 font-medium text-gray-800 border-r border-gray-200 bg-blue-100">
                    <div className="flex flex-col">
                      <div className="h-4 bg-gradient-to-r from-blue-200 to-blue-300 rounded animate-pulse w-24"></div>
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded animate-pulse w-16 mt-1"></div>
                    </div>
                  </td>
                  {/* Generate 10 skeleton slot cells */}
                  {[...Array(10)].map((_, slotIndex) => (
                    <td
                      key={slotIndex}
                      className="text-center py-2 px-2 text-xs font-semibold border border-gray-200">
                      <div className="h-3 bg-gradient-to-r from-blue-100 to-blue-200 rounded animate-pulse w-full"></div>
                    </td>
                  ))}
                </tr>
                {/* Value row skeleton */}
                <tr className="bg-white border-b border-gray-200">
                  {/* Generate 10 skeleton value cells */}
                  {[...Array(10)].map((_, slotIndex) => (
                    <td
                      key={slotIndex}
                      className="text-center p-2 text-xs font-semibold border border-gray-200">
                      <div className="flex items-center justify-center">
                        <div className="h-5 w-5 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full animate-pulse"></div>
                      </div>
                    </td>
                  ))}
                </tr>
                {/* Spacer row */}
                <tr className="h-2 bg-gradient-to-r from-blue-50 to-white">
                  <td colSpan={11} className="border-0"></td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full overflow-x-auto shadow-sm rounded-lg">
      {isLoading ? (
        <SkeletonTable />
      ) : (
        <table className="w-full border-collapse">
          <tbody>
            {timeSlots.map((timeSlot, timeIndex) => {
              return (
                <React.Fragment key={timeIndex}>
                  {/* Header row with profession IDs */}
                  <tr className="bg-blue-50 border-b border-gray-200">
                    <td
                      rowSpan="2"
                      className="px-4 py-3 font-medium text-gray-50 border-r border-gray-200 bg-blue-100 w-40">
                      <div className="flex flex-col">
                        <span className="font-semibold">{timeSlot.label}</span>
                        <div className="text-xs text-gray-500 mt-1">
                          {(() => {
                            const totalParticipants = timeSlot.professionSlots.reduce(
                              (total, slot) => total + (slot.totalParticipants || 0),
                              0,
                            );
                            const filledSlots = timeSlot.professionSlots.reduce(
                              (total, slot) => total + (slot.filledSlots || 0),
                              0,
                            );
                            const totalSlots = timeSlot.professionSlots.length;

                            return (
                              <div className="flex flex-col space-y-0.5">
                                <div className="flex items-center space-x-1">
                                  <svg
                                    width="4"
                                    height="2"
                                    viewBox="0 0 4 2"
                                    className="flex-shrink-0">
                                    <rect width="4" height="2" rx="1" fill="currentColor" />
                                  </svg>
                                  <span>
                                    {filledSlots}/{totalSlots} slots
                                  </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <svg
                                    width="4"
                                    height="2"
                                    viewBox="0 0 4 2"
                                    className="flex-shrink-0">
                                    <rect width="4" height="2" rx="1" fill="currentColor" />
                                  </svg>
                                  <span>
                                    {totalParticipants > 0
                                      ? `${totalParticipants} participants`
                                      : '0 participants'}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    {timeSlot.professionSlots.map((slot, slotIndex) => {
                      const { textColor, bgColor, hoverBg } = getStyles(
                        slot.guidedTourType,
                        slot.filledSlots,
                      );
                      const tourType = guidedTourTypes.find((t) => t.value === slot.guidedTourType);

                      return (
                        <td
                          key={slotIndex}
                          style={{
                            color: tourType?.color || '',
                            backgroundColor: tourType?.color2 || '',
                          }}
                          className={`text-center py-2 px-2 text-xs font-semibold border border-gray-200 transition-colors duration-150 ${!tourType ? `${textColor} ${bgColor} ${hoverBg}` : 'hover:opacity-80'}`}>
                          {slot.professionId.toUpperCase()}
                        </td>
                      );
                    })}
                  </tr>
                  {/* Value row with filled slots */}
                  <tr className="bg-white border-b border-gray-200">
                    {timeSlot.professionSlots.map((slot, slotIndex) => {
                      const { textColor, bgColor, hoverBg } = getStyles(
                        slot.guidedTourType,
                        slot.filledSlots,
                      );
                      const tourType = guidedTourTypes.find((t) => t.value === slot.guidedTourType);

                      return (
                        <td
                          key={slotIndex}
                          style={{
                            color: tourType?.color || '',
                            backgroundColor: tourType?.color ? `${tourType.color}10` : '',
                          }}
                          className={`text-center p-2 text-xs font-semibold border border-gray-200 transition-colors duration-150 ${!tourType ? `${textColor} ${bgColor} ${hoverBg}` : 'hover:opacity-80'}`}>
                          <div className="flex items-center justify-center">
                            {slot.totalParticipants !== undefined ? (
                              <span
                                className={`inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full text-xs font-semibold ${
                                  slot.totalParticipants > 0
                                    ? 'bg-gray-50 text-slate-100'
                                    : 'bg-gray-100 text-gray-500'
                                }`}>
                                {slot.totalParticipants || '0'}
                              </span>
                            ) : (
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${slot.filledSlots > 0 ? 'bg-gray-50 p-1' : 'bg-transparent'}`}>
                                {slot.filledSlots === 0 ? '-' : <CheckIcon />}
                              </span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Spacer row with subtle gradient */}
                  <tr className="h-2 bg-gradient-to-r from-blue-50 to-white">
                    <td colSpan={timeSlot.professionSlots.length + 1} className="border-0"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default GuidedTimeSlots;
